import { Injectable } from '@angular/core';
import { PaymentInterval, IntervalBase } from '@fmnts/api/next';
import { Decimal } from '@fmnts/core/decimal';

/**
 * Interface that represents an `amount` in respect to a
 * certain `base`.
 *
 * @example
 * // An amount of 12 per year
 * { amount: new Decimal(12), base: 'yearly' }
 */
export interface IntervalAmount {
  /** Amount in respect to the `base` */
  amount: Decimal;
  /** Base of the given `amount` */
  base: IntervalBase;
}

/**
 * Service for transforming and working with donation amounts.
 *
 * **ATTENTION**: This service uses the `Decimal` type for
 * numeric representations. It won't accept `number`s as inputs.
 */
@Injectable({
  providedIn: 'root',
})
export class DonationAmountService {
  /**
   * Factors used for conversion between intervals
   */
  private readonly baseFactors: { [k in PaymentInterval]: number } = {
    [PaymentInterval.Monthly]: 1,
    [PaymentInterval.Quarterly]: 3,
    [PaymentInterval.HalfYearly]: 6,
    [PaymentInterval.Yearly]: 12,
    [PaymentInterval.OneOff]: 1,
  };

  /**
   * @param amount The amount to transform
   * @param sourceInterval The interval for which the given amount accounts
   * @param targetInterval The interval to which the amount should be transformed
   *
   * @returns
   * The `amount` as interpreted with the given `sourceInterval` and transformed
   * to the given `targetInterval`.
   *
   * @example
   * // 10 per month, should be 120 yearly
   * transform(new Decimal(12), PaymentInterval.Monthly, PaymentInterval.Yearly)
   * === 10 * 12
   */
  public transform(
    amount: Decimal,
    sourceInterval: PaymentInterval,
    targetInterval: PaymentInterval,
  ) {
    return amount
      .times(this.getFactor(sourceInterval, targetInterval))
      .todp(2, Decimal.ROUND_HALF_CEIL);
  }

  /**
   * @param amount The amount to transform
   * @param sourceInterval The interval for which the given amount accounts
   * @param targetBase The base to which the amount should be transformed
   *
   * @returns
   * The `amount` as interpreted with the given `sourceInterval` and transformed
   * to the given `targetBase`.
   *
   * @example
   * // 10 per month, should be 120 yearly
   * toBase(new Decimal(10), PaymentInterval.Monthly, IntervalBase.Yearly)
   * === 10 * 12
   *
   * @example
   * // with base "PerInterval" there is no transformation
   * toBase(value, PaymentInterval.Monthly, IntervalBase.PerInterval) === value
   */
  public toBase(
    amount: Decimal,
    sourceInterval: PaymentInterval,
    targetBase: IntervalBase,
  ) {
    if (
      PaymentInterval.OneOff === sourceInterval ||
      IntervalBase.PerInterval === targetBase
    ) {
      return amount;
    }

    return this.transform(
      amount,
      sourceInterval,
      this.baseToInterval(targetBase),
    );
  }

  /**
   * @param amount The amount to transform
   * @param sourceBase The base for which the given amount accounts
   * @param targetInterval The interval to which the amount should be transformed
   *
   * @returns
   * The `amount` as interpreted with the given `sourceBase` and transformed
   * to the given `targetInterval`.
   *
   * @example
   * // 120 yearly, should be 12 per month
   * toInterval(new Decimal(120), IntervalBase.Yearly, PaymentInterval.Monthly)
   * === 120 / 12
   *
   * @example
   * // with base "PerInterval" no transformation should happen
   * toInterval(value, IntervalBase.PerInterval, PaymentInterval.Monthly) === value
   */
  public toInterval(
    amount: Decimal,
    sourceBase: IntervalBase,
    targetInterval: PaymentInterval,
  ) {
    if (
      PaymentInterval.OneOff === targetInterval ||
      IntervalBase.PerInterval === sourceBase
    ) {
      return amount;
    }

    return this.transform(
      amount,
      this.baseToInterval(sourceBase),
      targetInterval,
    );
  }

  /**
   * @param sourceInterval The source interval
   * @param targetInterval The target interval
   *
   * @returns
   * The factor, by which an amount from the given `sourceInterval` must be
   * multiplied, to get the amount for the given `targetInterval`.
   */
  private getFactor(
    sourceInterval: PaymentInterval,
    targetInterval: PaymentInterval,
  ) {
    const { baseFactors: factorMap } = this;
    if (!(sourceInterval in factorMap)) {
      throw new Error(`Unknown source interval: ${sourceInterval}`);
    }

    if (!(targetInterval in factorMap)) {
      throw new Error(`Unknown target interval: ${targetInterval}`);
    }

    // No conversion needed between same intervals
    if (sourceInterval === targetInterval) {
      return new Decimal(1);
    }

    // Conversion between OneOff and any other doesn't make sense
    if (
      PaymentInterval.OneOff === sourceInterval ||
      PaymentInterval.OneOff === targetInterval
    ) {
      throw new Error(`Unknown target interval: ${targetInterval}`);
    }

    return new Decimal(factorMap[targetInterval]).div(
      factorMap[sourceInterval],
    );
  }

  /**
   * @param base The base
   *
   * @returns
   * The corresponding interval for the given `base`
   */
  private baseToInterval(base: IntervalBase): PaymentInterval {
    switch (base) {
      case IntervalBase.Monthly:
        return PaymentInterval.Monthly;
      case IntervalBase.Yearly:
        return PaymentInterval.Yearly;
      default:
        throw new Error(`No mapping for interval base: ${base}`);
    }
  }
}
